import { useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Envs,
  deviceUtils,
  useAppBridge,
  useAppContext,
  useCurrentMenu,
  useDeviceBridge,
  useEnvContext,
  useGlobalErrorStore,
  useMenuRouter,
  useThrottle,
} from 'scbk-services';
import {
  Dialog,
  SecureKeypadFieldOption,
  SecureKeypadResult,
  useSecureKeypad,
  useSession,
  useTokensStore,
} from 'scbk-shared-module';
import { useCode } from 'scbk-shared-module/modules/Code';
import { filterBuilder, rulesBuilder, usePageForm } from 'scbk-shared-module/modules/Form';
import {
  TermsComponent,
  TermsParameter,
  TermsType,
  WrapConsentListProps,
  WrapSubCheckboxProps,
  useTerms,
} from 'scbk-shared-module/modules/Terms';
import {
  AccordionUnder,
  BottomButtons,
  BulletList,
  BulletListItem,
  Checkbox,
  ConsentRow,
  ConsentRowWrapper,
  ConsentTop,
  ConsentWrapper,
  FieldRound,
  FieldWrapper,
  Images,
  NativeSelect,
  NativeSelectOption,
  PhoneField,
  RoundListItem,
} from 'scbk-ui-web';
import {
  useAutVrfAddApplySimple,
  useAutVrfAddValidateSimple,
} from '../hooks/api/autVrfAdditionalHooks';
import { AutVrfAddApplySimpleRequest } from '../types/api/autVrfAddApplySimple';
import { AutVrfAddValidateSimpleRequest } from '../types/api/autVrfAddValidateSimple';
import { TermsUtils } from '../utils/AdditionalUtils';

interface ValidProps {
  terms: {
    agree1: boolean;
    agree2: boolean;
    agree3: boolean;
    agree4: boolean;
    agree5: boolean;
    agree5_1_1: boolean;
    agree5_1_2: boolean;
  };
  custName?: string; // 고객명
  perBusNo1?: string; //주민번호 앞자리
  perBusNo2?: string; //주민번호 뒷자리
  telecom: string; //통신사
  phoneNo1: string;
  phoneNo2: string;
  phoneNo3: string;
}

interface Props {
  onComplete?: () => void;
  isLoading?: boolean;
}

const AdditionalSimpleComponent: React.FC<Props> = ({ onComplete, isLoading = false }) => {
  const [isValidJoin, setIsValidJoin] = useState(false);
  const [isTermsView, setIsTermsView] = useState(false);

  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  const { getContext: appContext } = useAppContext();
  const { isLogin } = useSession();
  const { getEnv } = useEnvContext();
  const { currentMenuId } = useCurrentMenu(); // 현재 menu
  const { goMenu } = useMenuRouter();
  // 다국어 처리
  const { t } = useTranslation();

  const { doNPermissionCheck } = useAppBridge();
  const { doNRegistUsimMo } = useDeviceBridge();

  const terms = useTerms();
  const { selectCodes } = useCode(['CELLULARPRE', 'TELECOM']);

  const { setErrorInfo } = useGlobalErrorStore.getState();

  const { setChangeSmsAuthYn } = useTokensStore();

  const simpleAgreeList: WrapConsentListProps[] = [
    {
      id: 'terms.agree1',
      name: 'terms.agree1',
      title: t('AdditionalComponent.056', '[필수] 이동통신사별 서비스 이용약관'),
      hasCheckbox: true,
      hasConfirm: false,
      active: false,
      prvsnCd: 'PRVCMM000287',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree2',
      name: 'terms.agree2',
      title: t('AdditionalComponent.057', '[필수] 서비스 이용약관 동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM002002',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree3',
      name: 'terms.agree3',
      title: t('AdditionalComponent.058', '[필수] 개인정보 수집 및 이용 동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM000299',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree4',
      name: 'terms.agree4',
      title: t('AdditionalComponent.059', '[필수] 개인정보 제공 및 위탁동의'),
      hasCheckbox: true,
      active: false,
      prvsnCd: 'PRVCMM002001',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree5',
      name: 'terms.agree5',
      title: t('AdditionalComponent.060', '[필수] 개인(신용)정보 수집·이용 동의서'),
      subTitle: t('AdditionalComponent.061', '[비여신금융거래용]'),
      hasCheckbox: true,
      active: false,
      depth: 1,
      agreeNumber: 5,
      groupId: 'agree5',
      prvsnCd: 'PRVCMM000202',
      termsType: TermsType.HTML_EXPLAIN,
    },
    {
      id: 'terms.agree5_1',
      name: 'terms.agree5_1',
      title: t('AdditionalComponent.062', '수집·이용에 관한 사항'),
      hasCheckbox: false,
      active: false,
      groupId: 'agree5',
      depth: 2,
      agreeNumber: 1,
      prvsnCd: 'PRVCMM000202',
      termsType: TermsType.HTML_EXPLAIN,
      subCheckbox: [
        {
          id: 'terms.agree5_1_1',
          name: 'terms.agree5_1_1',
          title: t('AdditionalComponent.063', '고유식별정보 수집·이용 동의'),
          groupId: 'agree5',
          depth: 3,
          agreeNumber: 1,
          prvsnCd: 'PRVCMM000202',
          termsType: TermsType.HTML_EXPLAIN,
        },
        {
          id: 'terms.agree5_1_2',
          name: 'terms.agree5_1_2',
          title: t('AdditionalComponent.064', '개인(신용)정보 수집·이용 동의'),
          groupId: 'agree5',
          depth: 3,
          agreeNumber: 1,
          prvsnCd: 'PRVCMM000202',
          termsType: TermsType.HTML_EXPLAIN,
        },
      ],
    },
  ];

  const { submit, isError, getFieldError, useFields, setValue, getValues, setFocus } =
    usePageForm<ValidProps>({
      defaultValues: {
        terms: {
          agree1: false,
          agree2: false,
          agree3: false,
          agree4: false,
          agree5: false,
          agree5_1_1: false,
          agree5_1_2: false,
        },
        custName: '', // 고객명
        perBusNo1: '',
        perBusNo2: '',
        telecom: '',
        phoneNo1: '010',
        phoneNo2: '',
        phoneNo3: '',
      },
      onSubmitError: (error) => {
        console.log('# onSubmitError ', error);
      },
    });

  const fields = useFields({
    'terms.agree1': {
      rules: rulesBuilder<ValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree2': {
      rules: rulesBuilder<ValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree3': {
      rules: rulesBuilder<ValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree4': {
      rules: rulesBuilder<ValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    'terms.agree5': {
      rules: rulesBuilder<ValidProps>()
        .validate((v) => {
          return v === 'true' ? true : t('AdditionalComponent.065', '약관에 동의해 주세요.');
        })
        .build(),
    },
    ...(!isLogin() && {
      custName: {
        rules: rulesBuilder<ValidProps>()
          .required(t('AdditionalComponent.066', '이름을 입력해 주세요.'))
          .build(),
      },
      perBusNo1: {
        rules: rulesBuilder<ValidProps>()
          .required(t('AdditionalComponent.068', '주민등록번호를 입력해 주세요.'))
          .number()
          .minLength(6, t('AdditionalComponent.069', '주민등록번호 앞 6자리를 입력해 주세요.'))
          .build(),
      },
      perBusNo2: {
        rules: rulesBuilder<ValidProps>()
          .validate((v, ctx) => {
            const el = document.getElementById(ctx.name) as HTMLInputElement;
            const value = v !== '' ? v : (el?.value ?? '');
            if (value.length === 0) {
              return t('AdditionalComponent.070', '주민등록번호 뒷자리를 입력해 주세요.');
            }
            return value.length === 7
              ? true
              : t('AdditionalComponent.071', '주민등록번호 뒤 7자리를 입력해 주세요.');
          })
          .build(),
      },
    }),
    telecom: {
      rules: rulesBuilder<ValidProps>()
        .required(t('AdditionalComponent.072', '통신사를 선택해 주세요.'))
        .build(),
    },
    phoneNo1: {
      rules: rulesBuilder<ValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .build(),
    },
    phoneNo2: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<ValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .minLength(4, t('AdditionalComponent.109', '휴대폰번호는 최소 8자리로 입력해 주세요.'))
        .build(),
    },
    phoneNo3: {
      filters: filterBuilder().number().build(),
      rules: rulesBuilder<ValidProps>()
        .required(t('AdditionalComponent.073', '휴대폰번호를 입력해 주세요.'))
        .minLength(4, t('AdditionalComponent.109', '휴대폰번호는 최소 8자리로 입력해 주세요.'))
        .build(),
    },
  });

  // 약관 (S)
  const [termsAllChecked, setTermsAllChecked] = useState(false);

  const utils = useMemo(
    () => TermsUtils<typeof fields>(setValue, getValues, simpleAgreeList, setTermsAllChecked),
    [setValue, getValues, simpleAgreeList, termsAllChecked, setTermsAllChecked],
  );

  const openTerms = ({
    index,
    subIndex,
    active,
  }: {
    index: number;
    subIndex?: number;
    active?: boolean;
  }) => {
    const info: WrapSubCheckboxProps | WrapConsentListProps | undefined =
      subIndex !== undefined
        ? simpleAgreeList[index]?.subCheckbox?.[subIndex]
        : simpleAgreeList[index];

    if (!info) return;

    if (!active) {
      terms.openTerms({
        ...(info as TermsParameter),
        onClose: () => {
          utils.setChecked({ index, subIndex, active: true });
        },
      });
    } else {
      utils.setChecked({ index, subIndex, active: false });
    }
  };

  const isTermCurrentIdx = useRef(0);
  const openTermsAll = useCallback(async () => {
    const filteredList = simpleAgreeList.filter((item) => item.hasCheckbox);
    isTermCurrentIdx.current = 0;

    if (!termsAllChecked) {
      setIsTermsView(true);
      //   terms.openTerms({
      //     ...simpleAgreeList[4],
      //     onClose: () => {
      //       utils.setAllChecked(true);
      //     },
      //   });
      const openNext = () => {
        const current = filteredList[isTermCurrentIdx.current];
        if (!current) {
          setTimeout(() => {
            setIsTermsView(false);
          }, 2000);

          if (isLogin()) {
            requestAnimationFrame(() => {
              setFocus('telecom');
            });
          } else {
            requestAnimationFrame(() => {
              setFocus('custName');
            });
          }
          return;
        }

        if (getValues(current.id as keyof typeof fields) === 'true') {
          isTermCurrentIdx.current++;
          openNext();
          return;
        }

        current.triggerCloseEvent = true;
        terms.openTerms({
          ...current,
          onClose: (result) => {
            if (result) {
              utils.setChecked({ index: isTermCurrentIdx.current, active: true });
              isTermCurrentIdx.current++;
              openNext();
            } else {
              setTimeout(() => {
                setIsTermsView(false);
              }, 2000);
            }
          },
        });
      };
      openNext();
    } else {
      utils.setAllChecked(false);
    }
  }, [simpleAgreeList, termsAllChecked]);
  // 약관 (E)

  /**
   * 키패드 정보 배열
   */
  const secureKeypadList: SecureKeypadFieldOption[] = [
    {
      fieldId: 'perBusNo2', // input id
      // aesEnc: '0',
      onComplete: (result: SecureKeypadResult) => {
        // 가상키패드 입력 완료 시 호출 함수
        requestAnimationFrame(() => {
          setFocus('telecom');
        });

        console.log(result);
      },
    },
  ];

  /**
   * useSecureKeypad hooks 선언
   */
  const secureKeypad = useSecureKeypad(secureKeypadList);

  const { requestWithSecure: applySimple } = useAutVrfAddApplySimple({
    onError: (error) => {
      setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const { request: validateSimple } = useAutVrfAddValidateSimple({
    onError: (error) => {
      setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const confirmSimple = useCallback(async () => {
    const request: AutVrfAddApplySimpleRequest = {
      telecom: getValues('telecom'),
      telNo: getValues('phoneNo1') + getValues('phoneNo2') + getValues('phoneNo3'),
      custName: getValues('custName') ?? '',
      perBusNo1: getValues('perBusNo1') ?? '',
      privacySharingAgreeYn: 'Y', //개인정보 이용 동의여부
      thirdPartyProvisionAgreeYn: 'Y', //개인정보 제3자 제공 동의여부
    };

    const result = await applySimple(request);

    if (result?.authYn === 'Y' && result?.resultCode === '0000') {
      if (result?.yohumgb === 'Y') {
        // CRTJCT001_10MS - 공동인증서 신규/재발급
        // CRTFCT001_10MS - 금융인증서 신규/재발급
        // CRTDCT001_10MS - 디지털인증서 발급/재발급
        // CRTATH001_10MS - OTP 등록/해제
        if (
          currentMenuId === 'CRTJCT001_10MS' ||
          currentMenuId === 'CRTFCT001_10MS' ||
          currentMenuId === 'CRTDCT001_10MS' ||
          currentMenuId === 'CRTATH001_10MS'
        ) {
          const isOk = await Dialog.confirm({
            content: [
              t('AdditionalComponent.110', '휴면계좌를 활성화 하셔야만 서비스 이용이 가능해요.'),
              t('AdditionalComponent.111', '휴면계좌 활성화 메뉴로 이동하시겠어요?'),
            ],
          });

          if (isOk) {
            //휴면계좌 활성화
            goMenu('DPSACC004_10MS');
          }
        } else {
          const isOK = await Dialog.alert({
            content: [
              t(
                'AdditionalComponent.112',
                '휴면계좌를 보유하고 있어요. 영업점 방문 또는 모바일뱅킹으로 해제할 수 있어요.',
              ),
              t('AdditionalComponent.113', '인증/지원센터>휴면계좌 활성화'),
            ],
          });

          if (isOK) {
            confirmBtnRef.current?.focus();
          }
        }
      } else {
        onComplete?.();
      }
    } else {
      //SMS 명의인증변환여부 - 디지털인증서 발급시에 법인폰 사용자
      if (result?.changeSmsAuthYn === 'Y') {
        const isOk = await Dialog.alert({
          content: t(
            'AdditionalComponent.075',
            '통신사에 법인폰 본인인증 서비스를 가입하신 고객만 휴대폰 본인인증을 통해 진행이 가능합니다.',
          ),
        });

        if (isOk) {
          //휴대폰본인인증 으로 변경해야됨.
          setChangeSmsAuthYn('Y');
          return;
        }
      }

      //기기 미등록
      if (result?.resultCode === '9999') {
        //TODO: 스마트폰에서 접근시 간편인증 가입절차 추가

        if (appContext().channel == 'MB') {
          setIsValidJoin(true);
        } else {
          Dialog.alert({
            content: t('AdditionalComponent.076', '스마트폰 등록 후 간편인증이 가능합니다.'),
          });
        }
      } else {
        Dialog.alert({ content: result?.resultMsg });
      }
    }
  }, []);

  const doUsimMo = async () => {
    const workId = '0810'; //업무아이디
    const telNum = getEnv() === Envs.PROD ? '15553080990' : '1666-9341';

    const response = await doNRegistUsimMo(workId, telNum);

    if (!response) {
      return;
    }

    if (response?.result === '0') {
      //MO문자메세지 전송 후 agent를 통해 db에 적재되기 까지 약간의 시간이 필요하여 딜레이줌
      setTimeout(async () => {
        const request: AutVrfAddValidateSimpleRequest = {
          telNo: getValues('phoneNo1') + getValues('phoneNo2') + getValues('phoneNo3'),
          moNumber: telNum,
        };

        const validate = await validateSimple(request);

        const msg =
          validate?.resultYn === 'Y'
            ? t(
                'AdditionalComponent.077',
                `단말기기가 등록되었습니다. '인증확인' 버튼을 눌러주세요.`,
              )
            : t(
                'AdditionalComponent.078',
                '잠시후 다시 시도 부탁드립니다. (개인명의의 휴대폰이 아닌 경우 이용이 불가합니다.)',
              );

        const isOk = await Dialog.alert({
          content: msg,
        });

        if (isOk) {
          setIsValidJoin(false);
          //keypad 재설정 (초기화방지)
          secureKeypad.setKeypadEncData();
        }
      }, 2000);
    } else {
      setTimeout(function () {
        //단말등록이 실패했습니다. 다시 진행해 주세요.
        Dialog.alert({
          content: t('AdditionalComponent.079', '단말등록이 실패했습니다. 다시 진행해 주세요.'),
        });
      }, 100);
    }
  };

  const throttleClick = useThrottle(() => {
    console.log('hdg AdditionalSimplecomponent throttleClick !!!!');
    handleSubmit();
  }, 1000);

  const handleSubmit = submit(async () => {
    confirmSimple();
  });

  const handleRegister = async () => {
    //1. doNRegistUsimMo
    //2. MA3CMMAAT004_201S

    if (deviceUtils.isAndroid()) {
      const result = await doNPermissionCheck('2'); //type >> 0:위치, 1:카메라, 2:주소록, 3:SMS
      if (result == '0') {
        doUsimMo();
      }
    } else {
      doUsimMo();
    }
  };

  return (
    <>
      <div style={{ display: !isValidJoin ? 'contents' : 'none' }}>
        <h2 className="page-heading-title mb-8">
          {t('AdditionalComponent.022', '간편인증으로 본인 확인해 주세요')}
        </h2>
        <ConsentWrapper>
          <ConsentTop>
            <Checkbox
              label={t('AdditionalComponent.007', '전체동의')}
              id="check-all"
              value="check-all"
              checked={termsAllChecked}
              onClick={() => openTermsAll()}
            />
          </ConsentTop>
          <ConsentRowWrapper>
            {simpleAgreeList.map((item, idx) => (
              <ConsentRow
                key={`agree-list2-${idx}`}
                hasCheckbox={item.hasCheckbox}
                action={item.hasConfirm ? 'confirm' : 'arrow'}
                onClick={() => {
                  openTerms({
                    index: idx,
                    active: false,
                  });
                }}
                subCheckbox={item?.subCheckbox?.map((sub, subIdx) => (
                  <FieldWrapper key={`agree-list2-${idx}-${subIdx}`} isIndented>
                    <Checkbox
                      key={sub.id}
                      variants="icon"
                      label={sub.title}
                      id={sub.id}
                      weight="light"
                      checked={getValues(sub.id as keyof typeof fields) === 'true'}
                      onClick={() => {
                        openTerms({
                          index: idx,
                          subIndex: subIdx,
                          active: getValues(sub.id as keyof typeof fields) === 'true',
                        });
                      }}
                    />
                  </FieldWrapper>
                ))}
              >
                {item.hasCheckbox ? (
                  <FieldWrapper
                    isIndented
                    labelFor={item.id}
                    isError={isError(item.id as keyof typeof fields)}
                    helpMessage={getFieldError(item.id as keyof typeof fields)}
                  >
                    <Checkbox
                      label={item.title}
                      desc={item.subTitle}
                      id={item.id}
                      weight="light"
                      checked={getValues(item.id as keyof typeof fields) === 'true'}
                      onClick={() => {
                        openTerms({
                          index: idx,
                          active: getValues(item.id as keyof typeof fields) === 'true',
                        });
                      }}
                      ref={fields[item.id as keyof typeof fields].ref}
                    />
                  </FieldWrapper>
                ) : (
                  item.title
                )}
              </ConsentRow>
            ))}
          </ConsentRowWrapper>
        </ConsentWrapper>

        <div className="flex flex-col gap-6 mt-8">
          {!isLogin() && (
            <>
              <FieldWrapper
                labelFor="custName"
                title={t(`AdditionalComponent.008`, `성명`)}
                isError={isError('custName')}
                helpMessage={getFieldError('custName')}
              >
                <FieldRound
                  {...fields.custName.inputProps}
                  id="custName"
                  placeholder={t(`AdditionalComponent.009`, `실명(한글)입력`)}
                  maxLength={15}
                  value={getValues('custName')}
                  onChange={(e) => {
                    fields.custName.inputProps?.onChange(e);
                  }}
                  error={isError('custName')}
                />
              </FieldWrapper>

              <FieldWrapper
                title={t(`AdditionalComponent.010`, `주민등록번호`)}
                isError={isError('perBusNo1') || isError('perBusNo2')}
                helpMessage={getFieldError('perBusNo1') || getFieldError('perBusNo2')}
              >
                <div className="flex items-center gap-2">
                  <FieldRound
                    id="perBusNo1"
                    className="w-[50%]"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={t(`AdditionalComponent.011`, `앞 6자리`)}
                    aria-label={t(`AdditionalComponent.012`, `주민등록번호: 앞 6자리`)}
                    {...fields.perBusNo1.inputProps}
                    onChange={(e) => {
                      fields.perBusNo1.inputProps?.onChange(e);
                      if (e.target.value.length == 6) {
                        requestAnimationFrame(() => {
                          setFocus('perBusNo2');
                          secureKeypad.open('perBusNo2');
                        });
                      }
                    }}
                    error={isError('perBusNo1')}
                  />

                  <FieldRound
                    id="perBusNo2"
                    className="w-[50%]"
                    type="password"
                    data-enc="num" // 가상키패드 입력 타입 'num' : 숫자 키패드 / 'qwerty' : 문자키패드
                    data-aesenc="0"
                    maxLength={7}
                    placeholder={t(`AdditionalComponent.013`, `뒤 7자리`)}
                    aria-label={t(`AdditionalComponent.014`, `주민등록번호: 뒤 6자리`)}
                    ref={(el: HTMLInputElement) => {
                      const originRef = fields.perBusNo2.inputProps.ref;
                      originRef(el);
                      secureKeypad.ref?.(el);
                    }}
                    error={isError('perBusNo2')}
                  />
                </div>
              </FieldWrapper>
            </>
          )}

          <FieldWrapper
            labelFor="field-select2"
            title={t(`AdditionalComponent.015`, `휴대폰번호`)}
            isError={
              isError('telecom') ||
              isError('phoneNo1') ||
              isError('phoneNo2') ||
              isError('phoneNo3')
            }
            helpMessage={
              getFieldError('telecom') ||
              getFieldError('phoneNo1') ||
              getFieldError('phoneNo2') ||
              getFieldError('phoneNo3')
            }
          >
            <NativeSelect
              {...fields.telecom.inputProps}
              value={getValues('telecom')}
              onChange={(e) => {
                fields.telecom.inputProps?.onChange(e);
                if (!deviceUtils.isIOS()) {
                  setFocus('phoneNo2');
                }
              }}
              onBlur={(e) => {
                fields.telecom.inputProps?.onBlur(e);
                if (deviceUtils.isIOS()) {
                  setFocus('phoneNo2');
                }
              }}
              isValid={
                isError('telecom') ||
                isError('phoneNo1') ||
                isError('phoneNo2') ||
                isError('phoneNo3')
              }
            >
              {selectCodes('TELECOM') &&
                selectCodes('TELECOM')?.map((item, idx) => {
                  return (
                    <NativeSelectOption value={item.value} key={`telecom-${idx}-${item.value}`}>
                      {item.label}
                    </NativeSelectOption>
                  );
                })}
            </NativeSelect>

            <PhoneField
              id="phoneNo"
              selectOption={selectCodes('CELLULARPRE')}
              prefixSelect={{
                ...fields.phoneNo1.inputProps,
                value: getValues('phoneNo1'),
                onChange: (e) => {
                  fields.phoneNo1.inputProps?.onChange(e);
                  if (!deviceUtils.isIOS()) {
                    setFocus('phoneNo2');
                  }
                },
                onBlur: (e) => {
                  fields.phoneNo1.inputProps?.onBlur(e);
                  if (deviceUtils.isIOS()) {
                    setFocus('phoneNo2');
                  }
                },
              }}
              middleInput={{
                ...fields.phoneNo2.inputProps,
                value: getValues('phoneNo2'),
                onChange: (e) => {
                  fields.phoneNo2.inputProps?.onChange(e);
                  setValue('phoneNo2', e.target.value);
                  if (e.target.value.length == 4) {
                    requestAnimationFrame(() => {
                      setFocus('phoneNo3');
                    });
                  }
                },
              }}
              lastInput={{
                ...fields.phoneNo3.inputProps,
                value: getValues('phoneNo3'),
                onChange: (e) => {
                  fields.phoneNo3.inputProps?.onChange(e);
                  setValue('phoneNo3', e.target.value);
                },
              }}
              error={
                isError('telecom') ||
                isError('phoneNo1') ||
                isError('phoneNo2') ||
                isError('phoneNo3')
              }
            />
          </FieldWrapper>
        </div>

        <BottomButtons
          buttons={[
            {
              label: t('AdditionalComponent.023', '인증 확인'),
              ref: confirmBtnRef,
              onClick: () => {
                throttleClick();
              },
              Variants: 'primary',
              //   disabled: true,
              disabled: isLoading || isTermsView,
            },
          ]}
        />

        {/* 약관뷰어 공통 컴포넌트 */}
        {terms.termsPopup.isOpen && <TermsComponent {...terms} />}
      </div>
      <div style={{ display: isValidJoin ? 'contents' : 'none' }}>
        <h2 className="page-heading-title mb-8">
          <Trans
            t={t}
            i18nKey="AdditionalComponent.024"
            components={[<span className="block md:inline md:mr-1"></span>]}
          >
            <span className="block md:inline md:mr-1">서비스 이용을 위해</span>SMS를 전송해 주세요
          </Trans>
        </h2>
        <div className="step-list-wrap">
          {/* 260527 클래스 수정 */}
          <ul className="flex flex-col gap-3 step-list">
            <RoundListItem
              key={0}
              label={t('AdditionalComponent.025', 'SMS 전송요청')}
              description={t('AdditionalComponent.026', '[SMS 전송요청] 버튼을 눌러주세요.')}
            />
            <RoundListItem
              key={1}
              label={t('AdditionalComponent.027', '인증코드 입력')}
              description={t(
                'AdditionalComponent.028',
                '고객님 휴대폰의 메시지 화면에 인증코드가 자동으로 입력돼요.',
              )}
            />
            <RoundListItem
              key={2}
              label={t('AdditionalComponent.029', '인증코드 전송')}
              description={t(
                'AdditionalComponent.030',
                '인증코드를 수정 없이 그대로 전송해 주세요.',
              )}
            />
          </ul>
        </div>
        <div className="flex justify-center mt-8">
          <img
            src={Images.ImgSMS}
            alt={t('AdditionalComponent.031', 'SMS 인증 예시')}
            className="w-[338px] h-[284px]"
          />
        </div>

        <AccordionUnder openTitle={t('AdditionalComponent.032', '알아두세요')}>
          <BulletList type="circle">
            <BulletListItem>
              {t('AdditionalComponent.033', '앱을 최초 설치한 경우 단말기기 등록이 진행돼요.')}
            </BulletListItem>
            <BulletListItem>
              {t('AdditionalComponent.034', '인증코드를 수정하고 전송하면 실패해요.')}
            </BulletListItem>
            <BulletListItem>
              {t('AdditionalComponent.035', '인증코드는 제3자가 사용할 수 없어요.')}
            </BulletListItem>
            <BulletListItem>
              {t(
                'AdditionalComponent.036',
                '통신사 요금제에 따라 SMS전송비용(20원)이 부과될 수 있어요.',
              )}
            </BulletListItem>
            <BulletListItem>
              {t(
                'AdditionalComponent.037',
                '안전한 금융서비스를 위해 전화번호가 등록된 기기에서만 인증할 수 있어요.',
              )}
            </BulletListItem>
          </BulletList>
        </AccordionUnder>

        <BottomButtons
          buttons={[
            {
              label: t('AdditionalComponent.038', 'SMS 전송요청'),
              onClick: () => {
                handleRegister();
              },
              Variants: 'primary',
            },
          ]}
        />
      </div>
    </>
  );
};

export default AdditionalSimpleComponent;
